import { createFileRoute } from "@tanstack/react-router";
import { Container, Eyebrow, Lead, TextLink } from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";

const title = "Accessibility Statement | Scryla";
const description =
  "Scryla's WCAG 2.2 AA accessibility target, testing approach, known limitations and how to contact Timothy Ng about an accessibility barrier.";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}/accessibility` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/accessibility` }],
  }),
  component: AccessibilityPage,
});

function AccessibilityPage() {
  return (
    <Container>
      <Eyebrow>Accessibility</Eyebrow>
      <h1 className="mt-4 text-[34px] font-bold leading-tight md:text-5xl">
        Accessibility statement
      </h1>
      <Lead className="mt-5">
        Scryla aims to make this website usable by everyone, including people who use assistive
        technology.
      </Lead>
      <div className="mt-8 max-w-[70ch] space-y-8 text-base leading-relaxed text-body">
        <section aria-labelledby="accessibility-target">
          <h2 id="accessibility-target" className="text-2xl font-bold">
            Our target
          </h2>
          <p className="mt-3">
            Our target is the W3C Web Content Accessibility Guidelines (WCAG) 2.2 at Level AA. This
            is a target, not a claim of independently certified or complete conformance.
          </p>
          <p className="mt-3">
            <a
              className="font-semibold underline underline-offset-4"
              href="https://www.w3.org/TR/WCAG22/"
            >
              Read WCAG 2.2 on the W3C website
            </a>
            .
          </p>
        </section>
        <section aria-labelledby="accessibility-testing">
          <h2 id="accessibility-testing" className="text-2xl font-bold">
            How we check
          </h2>
          <p className="mt-3">
            We use automated accessibility checks alongside manual checks of keyboard navigation,
            visible focus, form labels and errors, and layouts at different screen sizes. Automated
            checks cannot establish full conformance; testing with screen readers and people with
            disabilities remains necessary.
          </p>
        </section>
        <section aria-labelledby="accessibility-limitations">
          <h2 id="accessibility-limitations" className="text-2xl font-bold">
            Known limitations
          </h2>
          <p className="mt-3">
            Enquiry forms use Cloudflare Turnstile, a third-party spam check. Its behaviour can
            depend on your browser and network, and we cannot guarantee that it works with every
            assistive-technology setup. Older archived articles have not all had a full manual
            accessibility review.
          </p>
          <p className="mt-3">
            If a spam check or any part of the site prevents you from contacting me, you can email
            me directly.
          </p>
        </section>
        <section aria-labelledby="accessibility-feedback">
          <h2 id="accessibility-feedback" className="text-2xl font-bold">
            Tell me about a barrier
          </h2>
          <p className="mt-3">
            Email{" "}
            <a className="font-semibold underline underline-offset-4" href={`mailto:${SITE.email}`}>
              {SITE.email}
            </a>
            . If you can, include the page address, what you were trying to do and any browser or
            assistive technology involved. Do not include sensitive information.
          </p>
          <p className="mt-4">
            <TextLink to="/contact">Other ways to contact Tim</TextLink>
          </p>
        </section>
        <p className="text-sm text-muted-foreground">
          Statement updated: <time dateTime="2026-10-19">19 October 2026</time>.
        </p>
      </div>
    </Container>
  );
}
